import * as React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from "react-router-dom";
import { Selection, CommandBarButton, DefaultButton, IContextualMenuItem, IObjectWithKey, IColumn, MessageBar, MessageBarType, IContextualMenuProps } from 'office-ui-fabric-react';
import { ApplicationState } from '../../store';
import {
    DetailsControlSettings, Dictionary, EntityType, IBaseEntity,
    IWithActiveSubViewId, UserPreferencesSettingsUpdate, SortDirection, TimelineControlSettings, ComparerBuilder, TimelineMapControlSettings, getCheckboxOptionsBasedOnCommands,
    IExtensibleEntity,
    IEditableExtensibleEntity,
    UpdateOptions,
} from "../../entities/common";
import ListMenu, { IWithViews, IWithColumns, IOutline } from './extensibleEntity/ListMenu';
import { HierarchyDetailsList, IDraggableEvents, IListProps, ListGroupingProps, OnItemRender, PersistDetailsList } from './extensibleEntity/EntityDetailsList';
import * as Metadata from '../../entities/Metadata';
import { ISubentity, isLinkedSubentity, urlParamsBuilder, ISubentitySectionActions, SubentityInfo, ISuggestedSubentity, ISuggestedSubentityProps } from "../../entities/Subentities";
import RemoveDialog from "./RemoveDialog";
import * as FieldsStore from '../../store/fields';
import * as FiltersStore from '../../store/filters';
import { isInReadonlyMode } from '../../store/User';
import { contains, CommonOperations } from '../../store/permissions';
import * as NotificationsStore from '../../store/NotificationsStore';
import { HierarchyTimelineList, ITimelineProps, PersistTimelineList } from './extensibleEntity/EntityTimelineList';
import { HierarchyTimelineMap, ITimelineMapProps, PersistTimelineMap } from './extensibleEntity/EntityTimelineMap';
import { ControlSpiner } from './Spinner';
import { toDictionaryById, notUndefined, toDictionaryByName, arraysEqual, filter as filterArr } from '../utils/common';
import { ClearFilterComponent, SectionControlPlaceholder } from './sectionsControl/SectionPlaceholder';
import { IHeader } from './ItemCreation';
import { IValidator } from '../../validation';
import { Dispatch, bindActionCreators } from 'redux';
import { EntityGroup } from './extensibleEntity/EntityGroupHeader';
import ScrollPositionFixer from '../utils/ScrollPositionFixer';
import { SourceType } from '../../store/ExternalEpmConnectStore';
import { SectionHierarchyManager } from '../utils/SectionHierarchyManager';
import { SortService } from '../../services/SortService';
import { FilterHelper, FilterHelperProps } from '../../store/subentities/filters';
import PngExporter, { buildExportToPngMenuItem, PngExportConfig } from './PngExporter';
import { PngExportControlDetails, actionCreators as PngExportActionCreators } from '../../store/PngExporterStore';
import SelectionExt from './SelectionExt';
import { CreatePanel, ISubentityPanelProps, GenericUpdatePanel, PanelEdits } from './SubentityPanel';
import { IWithActiveFilter } from '../../store/services/settingsService';
import { ListUserSettings, Views } from '../../store/services/viewSaver';
import { Styling } from './timeline/TimelineList';
import { IWithSubViews, withSubView } from './withSubView';
import { IMaybeWithFilter, WithFilterProps, isFilterChanged, withFilter } from './withFilter';
import FullscreenReMountComponent from './FullscreenReMountComponent';
import { buildActionsMenuItemWithItems, buildExtensionsMenuItem, csvImportExportIconProps } from './headerMenuItemBuilders';
import { MenuTitleBuilder } from '../MenuTitleBuilder';
import { ExtensionInfo, ExtensionPageLocation, findExtension } from '../../store/ExtensionStore';
import ExtensionPanel from './ExtensionPanel';
import RowMenuWithExtensions from './extensibleEntity/RowMenuWithExtensions';
import { withActivityLogTab } from '../audit/ActivityLogTab';
import { ViewService } from '../../services/ViewService';
import { IWithSearch, isSearchChanged, withSearch } from './withSearch';
import { SearchValue } from './SearchBox';
import { FlowChart, FlowChartProps } from './extensibleEntity/FlowChart';
import { ConfirmationDialog } from './ConfirmationDialog';
import { PPMFeatures, Subscription } from '../../store/Tenant';
import { SuggestedConfirmPanel } from './SubentityPanel/SuggestedConfirmPanel';
import { SuggestedHelper } from './SuggestedHelper';

enum DialogAction {
    None,
    Update,
    Add,
    Import
}

export type ViewConfig<TSubentity extends ISubentity> = {
    styling?: Styling;
    displayFields: string[];
    onDisplayFieldsChange?: (displayFields: string[]) => void;
    draggableEvents: IDraggableEvents<TSubentity>;
}

export type MenuButtonState<TSubentity extends ISubentity> = {
    name?: string;
    title?: string;
    hidden?: boolean | ((selectedItems: TSubentity[]) => boolean);
    disabled?: boolean;
    action?: (selectedItems?: TSubentity[]) => void;
}

export type SubentitiesListHierarchyContext<TSubentity extends ISubentity> = {
    filter: Metadata.IFilter<Metadata.BaseFilterValue>,
    prefilter?: Metadata.PreFilterOption<TSubentity>,
    search?: SearchValue
}

export type SelectedEntitiesActions = {
    exportToCsv?: IContextualMenuItem;
}

type ISubentitiesListProps<TSubentity extends ISubentity> = {
    sectionId: string;
    controlId: string;
    parentEntityId: string;
    parentEntityType?: EntityType;
    timelineProps?: ITimelineProps;
    timelineMapProps?: ITimelineMapProps;
    flowChartProps?: FlowChartProps;
    listStyling?: Styling;
    entityPanelHeader?: Partial<IHeader> | ((entity: TSubentity | undefined) => Partial<IHeader> | undefined);
    useViews?: boolean;
    useFilters?: boolean;
    hideConfigureColumns?: boolean;

    preFilterItems?: Metadata.PreFilterOption<TSubentity>[];
    sourceType?: SourceType;
    subentityInfo: SubentityInfo;
    settings: ISubentitiesListSettings;
    entities: TSubentity[];
    entitiesIsLoading?: boolean;
    entitiesIsUpdating?: boolean;
    actions: ISubentitySectionActions<TSubentity>;
    readOnly: boolean;
    allowExtensions?: boolean;
    canConfigure?: boolean;
    isArchived?: boolean;
    isViewSelected?: boolean;
    className?: string;
    getDeletionMessage?: (selectedItems: TSubentity[]) => string | undefined;
    renderDeleteDialogContent?: (selectedItems: TSubentity[]) => JSX.Element | undefined;
    fakeFields?: Metadata.Field[];
    fieldValueExtractor?: (item: TSubentity, field: Metadata.Field) => any;
    mandatoryEditFields?: string[];
    nonFilterableFields?: string[];
    readOnlyFields?: ((entity: TSubentity) => string[]) | string[];
    editableNativeFieldsForLinkedEntities?: ((entity: TSubentity) => string[]) | string[];
    theOnlyEditableFieldsForLinkedEntities?: string[];
    emptyScreen?: {
        iconName?: string;
        title?: string;
        description?: string | JSX.Element;
        onRenderCommands?: () => JSX.Element | null;
        className?: string;
    }
    editProps?: {
        uiControlElementsCustomRender?: Dictionary<any>;
        customFieldValidatorBuilder?: Dictionary<(state: any, field: Metadata.Field) => IValidator>;
        deriveDisplayAttributes?: (entity: any, changes: Dictionary<unknown>, edits?: PanelEdits) => Dictionary<unknown>;
        buildSavePayload?: (entity: any, changes: Dictionary<unknown>, derived: Dictionary<unknown>) => Dictionary<unknown>;
    }
    inlineEditProps?: {
        inlineEditing?: boolean;
        uiControlElementsCustomRender?: Dictionary<any>;
        customFieldValidatorBuilder?: Dictionary<(state: any, field: Metadata.Field) => IValidator>;
        nonApplicableFields?: string[];
    }
    renderImport?: (props: { onDismiss: () => void }) => JSX.Element | JSX.Element[] | null;
    listGrouping?: ListGroupingProps;
    groups?: EntityGroup[];
    showOnlyExternalCommands?: boolean;
    externalCommands?: {
        actionsCommandItems?: (filteredItems: TSubentity[]) => IContextualMenuItem[] | undefined;
        commands?: (filteredItems: TSubentity[]) => IContextualMenuItem[] | undefined;
        selectionModeCommands?: (selectedItems: TSubentity[], actions: SelectedEntitiesActions) => IContextualMenuItem[] | undefined;
    }
    extraColumns?: IColumn[];
    showMore?: {
        title?: string;
        count?: number;
        sliceFunc?: (entities: any[], showCount: number, getValue: (value: any) => TSubentity) => any[];
    };
    allowImportedManage?: boolean;
    hierarchy?: SectionHierarchyManager<TSubentity, SubentitiesListHierarchyContext<TSubentity>>;
    buildEntityFilterHelper?: (props: FilterHelperProps) => Metadata.IEntityFilterHelper<TSubentity>;

    entityPanelProps?: Partial<ISubentityPanelProps<TSubentity>>;
    onDelete?: (ids: string[]) => void;
    selection?: SelectionExt;
    onEditMenuClick?: (action: DialogAction, entity?: TSubentity) => void;

    controlSettings: SubListControlSettings;
    onSaveSettings?: (update: UserPreferencesSettingsUpdate, sendToServer?: boolean) => void;

    commandsConfig?: {
        add?: MenuButtonState<TSubentity> & { getSubMenuProps?: (defaultAction: () => void) => IContextualMenuProps };
        edit?: MenuButtonState<TSubentity>;
        delete?: MenuButtonState<TSubentity>;
        importFromCsv?: MenuButtonState<TSubentity>;
        exportToCsv?: MenuButtonState<TSubentity>;
        exportToPng?: MenuButtonState<TSubentity>;
        aiGeneration?: MenuButtonState<TSubentity>;
        outline?: {
            title?: () => string | undefined;
            disabled?: () => boolean;
        };
        columns?: {
            hiddenFields?: string[];
            flattenAvailableFieldGroups?: boolean;
            hideNewFieldButton?: boolean;
        }
    };
    isFullScreen: boolean;
    onItemRender?: OnItemRender<TSubentity>;
    onMenuRender?: () => JSX.Element;
    onViewContentRender?: (type: Views | 'Placeholder', defaultRender: () => JSX.Element) => JSX.Element;
    menu?: {
        isEnabled: boolean;
        mandatoryFields: string[];
        getItemCommands?: (selectedItems: TSubentity[], currentItem?: TSubentity, filterContext?: SubentitiesListHierarchyContext<TSubentity>) => IContextualMenuItem[];
    };
    resetExpandedAfterFilterChange?: boolean;
    suggestedInfo?: ISuggestedSubentityProps<TSubentity>;
    customView?: {
        isSelectionEnabled?: boolean;
        menu: IWithColumns & { farCommands?: IContextualMenuItem[] };
        config: ViewConfig<TSubentity>;
        render: (entities: TSubentity[]) => JSX.Element | null;
    }
};

type State<TSubentity extends ISubentity> = {
    listMinHeight?: number;
    action: DialogAction;
    removeItemIds?: string[];
    confirmSuggested?: ISubentity[];
    rejectSuggestedIds?: string[];
    selectedCount: number;
    isImportedEntitySelected: boolean;
    selectedEntityId?: string;
    filteredEntities: TSubentity[];
    isOpenExtensionPanel?: ExtensionInfo;
};

export type SubListControlSettings = {
    viewType: Views;
    details?: DetailsControlSettings & IWithActiveSubViewId;
    timelineMap?: TimelineMapControlSettings;
    timeline?: TimelineControlSettings & IWithActiveSubViewId;
} & IWithActiveFilter;

export interface ISuggestedDataContext<TSubentity extends ISubentity> {
    suggestedInfo?: ISuggestedSubentityProps<TSubentity & ISuggestedSubentity>;
}

export interface ISubentitiesListSettings {
    displayFields: string[];
    orderBy: string[];
    orderDisabled?: boolean;
    timeline?: {
        displayFields: string[];
    };
    timelineMap?: {
        displayFields: string[];
    };
}
export interface ISubentitySectionActionProps<TSubentity extends ISubentity> {
    buildNew?: () => TSubentity;
    save?: (item: TSubentity) => void;
    remove: (ids: string[]) => void;
    updateUIControl: (sectionId: string, uiControlId: string, settings: Dictionary<any>) => void;
    refreshEntity?: () => void;
    renderImport?: (props: { onDismiss: () => void }) => JSX.Element | JSX.Element[] | null;
    exportToFile?: (subentityType: EntityType, exportSubEntitiesToFile: string, fields: string[], ids: string[], viewType: 'Details' | 'Timeline') => void;
    importFromFile?: (subentityType: EntityType, subentityCollectionName: string | undefined, file: File) => void;
    dragEntities?: (entities: string[], groupid?: string, insertbeforeId?: string) => void;
}

type StoreProps = {
    fields: Metadata.Field[];
    isFieldsLoading: boolean;
    isLoading: boolean;
    pngExportDetails?: PngExportControlDetails;
    filters: Metadata.IFilter<Metadata.BaseFilterValue>[] | undefined;
    canManageConfiguration: boolean;
    hasSuggestions: boolean;
    hasAuditLog: boolean;
    canConfigureSelection: boolean;
    extensions: ExtensionInfo[];
};
type ActionProps = {
    fieldsActions: ReturnType<typeof FieldsStore.actionCreators.forEntity>;
    filtersActions: ReturnType<typeof FiltersStore.actionCreators.forEntity>;
    notificationsActions: typeof NotificationsStore.actionCreators;
    pngExporterActions: typeof PngExportActionCreators;
};
type OwnProps<TSubentity extends ISubentity> = ISubentitiesListProps<TSubentity>;
type Props<TSubentity extends ISubentity> = OwnProps<TSubentity>
    & StoreProps
    & ActionProps
    & RouteComponentProps<{}>
    & IMaybeWithFilter<TSubentity>
    & IWithSubViews
    & IWithSearch;

export interface IWithSelection {
    resetSelection: () => void;
}

class SubentitiesList<TSubentity extends ISubentity> extends React.Component<Props<TSubentity>, State<TSubentity>> {
    private _listContainer: HTMLDivElement | null = null;
    private _selection: Selection;
    private _fileUpload: HTMLInputElement | null | undefined;
    private _suggestedHelper: SuggestedHelper;
    private _updatePanel;

    constructor(props: Props<TSubentity>) {
        super(props);
        const selectedEntityId: string | undefined = this._getUrlEntityId(props);

        this.state = {
            action: selectedEntityId ? (selectedEntityId.toLowerCase() === Metadata.NEW_ID ? DialogAction.Add : DialogAction.Update) : DialogAction.None,
            selectedCount: 0,
            removeItemIds: undefined,
            isImportedEntitySelected: false,
            selectedEntityId,
            filteredEntities: []
        };

        const getKey = props.hierarchy?.getKey as (undefined | ((item: IObjectWithKey, index?: number) => string | number));
        props.selection?.AddEventHandler(this._onSelectionChanged);
        props.selection?.SetGetKey(getKey);
        this._selection = this.props.selection ?? new Selection({
            onSelectionChanged: this._onSelectionChanged,
            getKey
        });
        this._suggestedHelper = new SuggestedHelper(this._getPluralLabel(), props.suggestedInfo?.suggestedEntities);

        this._updatePanel = GenericUpdatePanel<TSubentity>();
    }

    componentWillUnmount() {
        this.props.selection?.RemoveEventHandler(this._onSelectionChanged);
    }

    componentWillMount() {
        if (this.props.suggestedInfo && this.props.hasSuggestions) {
            this.props.suggestedInfo.isLoading = true;
            this.props.suggestedInfo.actions.loadSuggested(this.props.parentEntityId, this.props.suggestedInfo.suggestedType);
        }
        if (this.props.isLoading) {
            return;
        }
        this._applyFilter(this.props);
    }

    componentDidUpdate(prevProps: Props<TSubentity>, prevState: State<TSubentity>) {
        if (this.state.listMinHeight !== undefined) {
            const delay = 300;
            //setTimeout is used as fabric DetailsList may render contents asyncronously
            setTimeout(() => {
                if (this.state.listMinHeight !== undefined) {
                    this.setState({ listMinHeight: undefined });
                }
            }, delay);
        }
    }

    componentWillReceiveProps(nextProps: Props<TSubentity>) {
        if (this.props.controlSettings.viewType !== nextProps.controlSettings.viewType) {
            this.setState({ listMinHeight: this._listContainer?.clientHeight });
        }

        if (!arraysEqual(this.props.fields, nextProps.fields)
            && this.props.suggestedInfo
            && this.props.hasSuggestions) {
            this.props.suggestedInfo.isLoading = true;
            this.props.suggestedInfo.actions.loadSuggested(this.props.parentEntityId, this.props.suggestedInfo.suggestedType);
        }

        const urlEntityId: string | undefined = this._getUrlEntityId(this.props);
        const nextUrlEntityId: string | undefined = this._getUrlEntityId(nextProps);
        if (urlEntityId !== nextUrlEntityId) {
            let selectedEntityId = nextUrlEntityId;
            if (!selectedEntityId) {
                const selected = this._selection.getSelection() as TSubentity[];
                const selectedEntity = selected.length === 1 ? selected[0] : undefined;
                selectedEntityId = selectedEntity?.id;
            }
            this.setState({ selectedEntityId });
            if (nextUrlEntityId) {
                this.setState({ action: nextUrlEntityId.toLowerCase() === Metadata.NEW_ID ? DialogAction.Add : DialogAction.Update });
            }
        }

        const filterChanged = isFilterChanged(this.props, nextProps);
        const searchChanged = isSearchChanged(this.props, nextProps);
        const isSuggestedChanged = nextProps.suggestedInfo?.suggestedEntities && this.props.suggestedInfo?.suggestedEntities?.length !== nextProps.suggestedInfo?.suggestedEntities.length;
        if (!arraysEqual(this.props.entities, nextProps.entities) || filterChanged || searchChanged || isSuggestedChanged) {
            this._applyFilter(nextProps, filterChanged || searchChanged);
            this._suggestedHelper = new SuggestedHelper(this._getPluralLabel(), nextProps.suggestedInfo?.suggestedEntities);
        }
    }

    private _applyFilter = (props: Props<TSubentity>, filterChanged?: boolean) => {
        const filteredEntities = filterArr(
            filterArr([...props.suggestedInfo?.suggestedEntities ?? [], ...props.entities], props.filter?.isItemVisible),
            props.search?.isItemVisible);

        if (props.hierarchy) {
            const context = {
                filter: props.filter?.activeFilter!,
                prefilter: props.filter?.preFilter?.active,
                search: props.search?.search
            }
            props.hierarchy.setItems(filteredEntities, props.entities, !(this.props.resetExpandedAfterFilterChange && filterChanged), context);
        }

        this.setState({ filteredEntities });
    }

    private _isHidden = (menu: MenuButtonState<TSubentity> | undefined, selectedItems: TSubentity[] | undefined = undefined): boolean => {
        if (!menu?.hidden) {
            return false;
        }
        if (typeof menu.hidden === 'function') {
            return menu.hidden(selectedItems ?? this._selection.getSelection() as TSubentity[]);
        }
        return menu.hidden;
    }

    private _onSelectionChanged = () => {
        const selected = this._selection.getSelection() as TSubentity[];
        const selectedEntity = selected.length === 1 ? selected[0] : undefined;

        this.setState({
            selectedEntityId: selectedEntity?.id,
            selectedCount: this._selection.getSelectedCount(),
            isImportedEntitySelected: selected.some(_ => this._isImported(_))
        });
    }

    private _getDisplayFields = (): string[] => {
        const { view, fields, fakeFields } = this.props;

        const allFields = [...fakeFields || [], ...fields];
        if (view) {
            if (!view.activeSubView) { return []; }

            const fieldsMap = toDictionaryById(allFields);
            return view.activeSubView?.columns.map(_ => fieldsMap[_.id]?.name).filter(notUndefined);
        }

        const fieldsByNameMap = toDictionaryByName(allFields);
        return this._getViewConfig().displayFields.map(_ => fieldsByNameMap[_]?.name).filter(notUndefined);
    }

    private _getUrlEntityId(props: Props<TSubentity>): string | undefined {
        const query = new URLSearchParams(props.location.search);
        return query.get(urlParamsBuilder.item(props.subentityInfo.subentityType)) || undefined;
    }

    private _clearFilter = () => {
        this.props.filter?.actions.onClearFilter(this.props?.filters)
        this.props.filter?.preFilter?.onChange(undefined);
    }

    render() {
        const { subentityInfo, hierarchy, fieldsActions, fields, fakeFields, mandatoryEditFields, readOnlyFields,
            editableNativeFieldsForLinkedEntities, theOnlyEditableFieldsForLinkedEntities, entities, isLoading,
            entitiesIsUpdating, readOnly, className, emptyScreen, fieldValueExtractor, editProps, inlineEditProps,
            menu, showMore, pngExportDetails, settings, controlSettings, onSaveSettings, onViewContentRender,
            extraColumns, commandsConfig, isFullScreen, filter, canManageConfiguration, isArchived, customView } = this.props;
        const { rejectSuggestedIds, confirmSuggested } = this.state;

        const { filteredEntities } = this.state;
        const allFields = [...fakeFields || [], ...fields];
        const fakeMap = toDictionaryById(fakeFields || []);

        if (isLoading) {
            return <ControlSpiner isLoading={isLoading} />;
        }

        const groups = this._suggestedHelper.getGroups(this.props.groups, this.props.entities?.length);
        const step = 10;
        const displayFields = this._getDisplayFields();
        const selectionModeCommands = this._getSelectionModeCommands(displayFields);
        const listProps: IListProps<TSubentity> & ListUserSettings = {
            isFilterApplied: filteredEntities.length !== entities.length,
            entityType: subentityInfo.subentityType,
            selection: this._selection,
            fields: allFields,
            isFieldFake: _ => !!fakeMap[_.id],
            displayFields,
            listGrouping: this._suggestedHelper.getListGrouping(!!pngExportDetails, this.props.listGrouping),
            groups,
            draggableEvents: pngExportDetails || readOnly || !hierarchy?.allowDrag()
                ? undefined
                : this._getViewConfig().draggableEvents,
            showMore: pngExportDetails?.isInProgress || isFullScreen
                ? undefined
                : { title: `Show ${step} more ${this._getPluralLabel()}`, step, ...showMore, extraCount: hierarchy?.getExpandedChildrenCount() },
            onItemRender: this.props.onItemRender,
            onItemMenuRender: menu?.isEnabled ? this._renderItemMenu : undefined,
            extraColumns: extraColumns,
            isVirtualizationDisabled: pngExportDetails?.isInProgress,
            fieldValueExtractor,
            entities: filteredEntities,
            defaultSort: settings.orderBy?.length
                ? { fieldName: settings.orderBy[0], direction: SortDirection.ASC }
                : undefined,
            sorting: settings.orderDisabled ? { disabled: true } : undefined,
            controlSettings: controlSettings,
            onSaveSettings: onSaveSettings,
            isArchived: isArchived,
            ...getCheckboxOptionsBasedOnCommands(selectionModeCommands),
            inlineEditProps: inlineEditProps?.inlineEditing && !readOnly
                ? {
                    onInlineEditComplete: (field: Metadata.Field, item: IExtensibleEntity, value: any, extraUpdates?: Dictionary<any>) => {
                        return Promise.resolve(this._onUpdate(item.id, ViewService.buildUpdates(field, value, extraUpdates), { inlineEdit: true }))
                    },
                    readonlyFields: readOnlyFields,
                    nonApplicableFields: inlineEditProps?.nonApplicableFields,
                    editableNativeFieldsForLinkedEntities,
                    theOnlyEditableFieldsForLinkedEntities,
                    customFieldValidatorBuilder: inlineEditProps?.customFieldValidatorBuilder ?? editProps?.customFieldValidatorBuilder,
                    uiControlElementsCustomRender: inlineEditProps?.uiControlElementsCustomRender ?? editProps?.uiControlElementsCustomRender,
                } : undefined
        };

        const menuProps = this._buildMenuProps(displayFields);

        const stylingMenuProps = this._buildStylingMenuProps();
        const outlineMenuProps = this._buildOutlineMenuProps();

        const selectedItem = this.state.action === DialogAction.Update ? this._getSelectedItem() : undefined;
        const isSuggestedItem = (selectedItem as unknown as ISuggestedSubentity)?.isSuggested;
        const isReadOnlyItem = (selectedItem as unknown as IEditableExtensibleEntity)?.isEditable === false;

        // Automatically inject Activity Log tab for all subentities
        const existingExtraTabs = this.props.entityPanelProps?.extraTabs || [];
        const extraTabsWithActivityLog = withActivityLogTab(
            this.props.hasAuditLog,
            subentityInfo.subentityType,
            subentityInfo.subentityTypeLabel,
            existingExtraTabs,
            this.props.parentEntityId,
            this.props.parentEntityType?.toString()
        );

        const panelProps = {
            subentityTypeLabel: subentityInfo.subentityTypeLabel,
            subentityType: subentityInfo.subentityType,
            fields: fields,
            displayFields,
            mandatoryEditFields,
            readOnlyFields,
            editableNativeFieldsForLinkedEntities,
            theOnlyEditableFieldsForLinkedEntities,
            header: typeof (this.props.entityPanelHeader) === "function" ? this.props.entityPanelHeader(selectedItem) : this.props.entityPanelHeader,
            onDismiss: this._onSubentityPanelClosed,
            uiControlElementsCustomRender: editProps?.uiControlElementsCustomRender,
            customFieldValidatorBuilder: editProps?.customFieldValidatorBuilder,
            deriveDisplayAttributes: editProps?.deriveDisplayAttributes,
            buildSavePayload: editProps?.buildSavePayload,
            readOnly: readOnly || isReadOnlyItem,
            parentEntityId: this.props.parentEntityId,
            parentEntityType: this.props.parentEntityType,
            ...this.props.entityPanelProps,
            extraTabs: extraTabsWithActivityLog
        }

        const viewContentRender = () => hierarchy
            ? <HierarchyViewSelector
                {...listProps}
                hierarchy={hierarchy}
                timelineMapProps={this._suggestedHelper.getTimelineMapProps(this.props.timelineMapProps)}
                timelineProps={this._suggestedHelper.getTimelineProps(this.props.timelineProps)}
                flowChartProps={this.props.flowChartProps}
                isFullScreen={isFullScreen}
                customViewRender={(viewType) => customView?.render(listProps.entities) ?? null}
            />
            : <ViewSelector
                {...listProps}
                timelineMapProps={this._suggestedHelper.getTimelineMapProps(this.props.timelineMapProps)}
                timelineProps={this._suggestedHelper.getTimelineProps(this.props.timelineProps)}
                flowChartProps={this.props.flowChartProps}
                isFullScreen={isFullScreen}
                customViewRender={(viewType) => customView?.render(listProps.entities) ?? null}
            />;

        const placeholderRender = () => <SectionControlPlaceholder
            key="no-data"
            iconName={emptyScreen!.iconName}
            title={emptyScreen!.title}
            description={emptyScreen!.description}
            className={emptyScreen!.className}>
            {
                !readOnly && <>
                    {
                        !this._isHidden(commandsConfig?.add) && <DefaultButton
                            text={commandsConfig?.add?.name ?? `New ${subentityInfo.subentityTypeLabel}`}
                            onClick={() => {
                                if (commandsConfig?.add?.action) {
                                    commandsConfig.add.action();
                                } else {
                                    this.setState({ action: DialogAction.Add });
                                    this.props.onEditMenuClick?.(DialogAction.Add);
                                }
                            }} />
                    }
                    {
                        !this._isHidden(commandsConfig?.importFromCsv) && <DefaultButton
                            text={commandsConfig?.importFromCsv?.name ?? `Import from File`}
                            onClick={() => this._fileUpload?.click()} />
                    }
                    {
                        this._isImportEnabled() &&
                        <DefaultButton
                            text={`Import ${this._getPluralLabel()}`}
                            onClick={() => this.setState({ action: DialogAction.Import })} />
                    }
                    {
                        !this._isHidden(commandsConfig?.aiGeneration) && <DefaultButton
                            text={commandsConfig?.aiGeneration?.name ?? 'Generate with AI'}
                            title={commandsConfig?.aiGeneration?.title}
                            disabled={commandsConfig?.aiGeneration?.disabled}
                            onClick={() => commandsConfig?.aiGeneration?.action?.()} />
                    }
                    {
                        emptyScreen!.onRenderCommands?.()
                    }
                </>
            }
        </SectionControlPlaceholder>;

        return <ControlSpiner isLoading={!!entitiesIsUpdating || !!this.props.suggestedInfo?.isLoading} className="show-over">
            {!entities.length && !this.props.suggestedInfo?.suggestedEntities?.length && !groups?.length && !!emptyScreen
                ? (onViewContentRender
                    ? onViewContentRender('Placeholder', placeholderRender)
                    : placeholderRender())
                : <ScrollPositionFixer>
                    {
                        //temporary setting div minHeight is needed because of the inner DetailsList anync render:
                        //it renders empty grid first and then adds the items. this causes page to shrink and scroll to jump
                    }
                    <div key="list" className={`subentities-list ${className || ""}`}
                        ref={_ => this._listContainer = _} style={{ minHeight: this.state.listMinHeight }}>
                    <ListMenu
                        {...menuProps}
                        {...filter?.menu}
                        {...stylingMenuProps}
                        selectionMode={{
                            enabled: this._isAnyItemSelected(),
                            items: selectionModeCommands,
                            onCancel: () => this._selection.setAllSelected(false)
                        }}
                        search={this.props.search}
                        styling={stylingMenuProps}
                        outline={outlineMenuProps}
                        entityType={subentityInfo.subentityType}
                        commands={this._getCommands()}
                        fields={listProps.fields}
                        isFieldFake={listProps.isFieldFake}
                        allowManageFields={!isArchived && canManageConfiguration}
                        fieldActions={fieldsActions}
                        mandatoryViewFields={menu?.mandatoryFields} />
                        <PngExporter details={pngExportDetails} getConfig={this._getPngExportConfig}>
                            <FullscreenReMountComponent isFullScreen={this.props.isFullScreen}>
                                <ClearFilterComponent
                                    items={this.props.entities}
                                    filteredItems={listProps.entities}
                                    onClearFilter={() => this._clearFilter()}
                                    search={this.props.search?.search}
                                >
                                    {
                                        onViewContentRender
                                            ? onViewContentRender(controlSettings.viewType, viewContentRender)
                                            : viewContentRender()
                                    }
                                </ClearFilterComponent>
                            </FullscreenReMountComponent>
                        </PngExporter>
                    </div>
                </ScrollPositionFixer>
            }
            {
                this.state.action === DialogAction.Add &&
                <CreatePanel
                    buildNewEntity={this.props.actions.buildNew}
                    onComplete={this._onCreate}
                    {...panelProps}
                />
            }
            {this.state.action === DialogAction.Update && selectedItem && !isSuggestedItem && (
                <this._updatePanel
                    key={selectedItem.id}
                    entity={selectedItem}
                    onDelete={
                        (this._isImported(selectedItem) && !this.props.allowImportedManage) || this._isHidden(commandsConfig?.delete) || readOnly
                            ? undefined
                            : (_) => this._onDeleteActionClick([_])
                    }
                    {...panelProps}
                    onComplete={this._onPanelUpdate}
                    onRefresh={this.props.actions.refreshEntity}
                />
            )}
            {this.state.action === DialogAction.Update && selectedItem && isSuggestedItem && (
                <SuggestedConfirmPanel
                    key={selectedItem.id}
                    entity={selectedItem}
                    {...panelProps}
                    onDismiss={confirmSuggested || rejectSuggestedIds ? () => { } : panelProps.onDismiss}
                    readOnly={readOnly}
                    onConfirmSuggested={(item) => this.setState({ confirmSuggested: [item] })}
                    onRejectSuggested={(item) => this.setState({ rejectSuggestedIds: [item.id] })}
                />
            )}

            {this.state.action === DialogAction.Import && this.props.renderImport?.({ onDismiss: () => this.setState({ action: DialogAction.None }) })}
            {
                this.state.removeItemIds?.length && <RemoveDialog
                    onClose={() => this.setState({ removeItemIds: undefined })}
                    onComplete={() => {
                        const { removeItemIds } = this.state;
                        if (removeItemIds?.length) {
                            if (this.props.onDelete) {
                                this.props.onDelete(removeItemIds);
                            } else {
                                this.props.actions.remove!(removeItemIds);
                            }
                        }
                        this._selection.setAllSelected(false);
                        this.setState({ removeItemIds: undefined });
                    }}
                    dialogContentProps={{
                        title: `Delete ${this.props.subentityInfo.subentityTypeLabel}`,
                        subText: this._getDeletionMessage()
                    }}
                    confirmButtonProps={{ text: "Delete" }}
                >
                    {this.props.renderDeleteDialogContent?.(entities.filter(_ => this.state.removeItemIds?.includes(_.id)))}
                </RemoveDialog>
            }
            {
                confirmSuggested?.length && <ConfirmationDialog
                    onDismiss={() => this.setState({ confirmSuggested: undefined })}
                    onYes={() => {
                        if (confirmSuggested?.length) {
                            this.props.suggestedInfo?.actions.confirmSuggested(this.props.parentEntityId, confirmSuggested, this.props.suggestedInfo.suggestedType);
                            this.props.notificationsActions.pushNotification({
                                message: `${confirmSuggested.length} suggested ${confirmSuggested?.length > 1 ? `${this._getPluralLabel()} are` : `${this.props.subentityInfo.subentityTypeLabel} is`} accepted and transformed into regular ${confirmSuggested?.length > 1 ? this._getPluralLabel() : this.props.subentityInfo.subentityTypeLabel} successfully`,
                                type: NotificationsStore.NotificationType.Success
                            });
                        }
                        this._selection.setAllSelected(false);
                        this.setState({ confirmSuggested: undefined });
                    }}
                    dialogContentProps={{
                        title: `Accept Suggested ${this.props.subentityInfo.subentityTypeLabel}`,
                        subText: `Are you sure you want to accept ${confirmSuggested.length} suggested ${confirmSuggested?.length > 1 ? this._getPluralLabel() : this.props.subentityInfo.subentityTypeLabel} and transform ${confirmSuggested?.length > 1 ? "them" : "it"} into regular ${confirmSuggested?.length > 1 ? this._getPluralLabel() : this.props.subentityInfo.subentityTypeLabel}?`
                    }}
                    yesButtonProps={{ text: "Accept" }}
                >
                </ConfirmationDialog>
            }
            {
                rejectSuggestedIds?.length && <ConfirmationDialog
                    onDismiss={() => this.setState({ rejectSuggestedIds: undefined })}
                    onYes={() => {
                        if (rejectSuggestedIds?.length) {
                            this.props.suggestedInfo?.actions.rejectSuggested(this.props.parentEntityId, rejectSuggestedIds, this.props.suggestedInfo.suggestedType);
                            this.props.notificationsActions.pushNotification({
                                message: `${rejectSuggestedIds.length} suggested ${rejectSuggestedIds?.length > 1 ? `${this._getPluralLabel()} are` : `${this.props.subentityInfo.subentityTypeLabel} is`} rejected successfully`,
                                type: NotificationsStore.NotificationType.Success
                            });
                        }
                        this._selection.setAllSelected(false);
                        this.setState({ rejectSuggestedIds: undefined });
                    }}
                    dialogContentProps={{
                        title: `Reject Suggested  ${this.props.subentityInfo.subentityTypeLabel}`,
                        subText: `Are you sure you want to reject ${rejectSuggestedIds.length} suggested ${rejectSuggestedIds?.length > 1 ? this._getPluralLabel() : this.props.subentityInfo.subentityTypeLabel} and delete ${rejectSuggestedIds?.length > 1 ? "them" : "it"} from the list?`
                    }}
                    yesButtonProps={{ text: "Reject" }}
                >
                </ConfirmationDialog>
            }

            {
                this.state.isOpenExtensionPanel &&
                <ExtensionPanel
                    entityType={this.props.subentityInfo.subentityType}
                    info={this.state.isOpenExtensionPanel}
                    context={{ entityId: this.props.parentEntityId, entityType: this.props.parentEntityType, sourceType: this.props.sourceType }}
                    onRefresh={this.props.actions.refreshEntity}
                    onDismiss={() => this.setState({ isOpenExtensionPanel: undefined })} />
            }
            <input ref={(ref) => this._fileUpload = ref} style={{ display: "none" }} type="file" accept=".csv"
                onChange={(e) => {
                    if (e.target && e.target.files && e.target.files[0]) {
                        this._onImportFromCsv(e.target.files[0]);
                    }
                    e.target.value = '';
                }} />
        </ControlSpiner >;
    }

    private _pendingSave?: Promise<unknown>;

    private _trackPendingSave = (result: void | Promise<unknown>) => {
        this._pendingSave = result instanceof Promise ? result : undefined;
        return result;
    }

    private _onCreate = (subentity: TSubentity) => this._trackPendingSave(this.props.actions.create!(subentity));

    private _onPanelUpdate = (id: string, changes: Dictionary<unknown>) => this._trackPendingSave(this.props.actions.update!(id, changes));

    private _onUpdate = (id: string, changes: Dictionary<unknown>, options?: UpdateOptions) => {
        return this.props.actions.update!(id, changes, options);
    }

    private _onSubentityPanelClosed = () => {
        if (this._pendingSave) {
            const pending = this._pendingSave;
            this._pendingSave = undefined;
            pending.then(() => this._closePanel(), () => { });
            return;
        }
        this._closePanel();
    }

    private _closePanel = () => {
        this.setState({ action: DialogAction.None });

        const query = new URLSearchParams(this.props.location.search);

        let urlChanged = false;

        if (query.has(urlParamsBuilder.item(this.props.subentityInfo.subentityType))) {
            query.delete(urlParamsBuilder.item(this.props.subentityInfo.subentityType));
            urlChanged = true;
        }

        if (query.has(urlParamsBuilder.itemType(this.props.subentityInfo.subentityType))) {
            query.delete(urlParamsBuilder.itemType(this.props.subentityInfo.subentityType));
            urlChanged = true;
        }

        if (urlChanged) {
            this.props.history.replace({ ...this.props.location, search: query.toString() });
        }
    }

    private _onDisplayFieldsChange = (displayFields: string[]) => {
        const { sectionId, controlId } = this.props;
        this.props.actions.updateUIControl!(sectionId, controlId, { displayFields });
    }

    private _onTimelineDisplayFieldsChange = (displayFields: string[]) => {
        const { sectionId, controlId } = this.props;
        this.props.actions.updateUIControl!(sectionId, controlId, { timeline: { displayFields } });
    }

    private _onTimelineMapDisplayFieldsChange = (displayFields: string[]) => {
        const { sectionId, controlId } = this.props;
        this.props.actions.updateUIControl!(sectionId, controlId, { timelineMap: { displayFields } });
    }

    private _getDeletionMessage = () => {
        const selectedItems = this.props.entities.filter(_ => this.state.removeItemIds?.includes(_.id));
        const msg = this.props.getDeletionMessage?.(selectedItems);
        if (msg) {
            return msg;
        }

        if (selectedItems.length === 1) {
            return `Are you sure you want to delete ${this.props.subentityInfo.subentityTypeLabel} "${selectedItems[0].attributes['Name']}"?`;
        }

        return `Are you sure you want to delete ${selectedItems.length} ${this._getPluralLabel()}?`;
    }

    private _getSelectedItem = (): TSubentity | undefined =>
        this.state.selectedEntityId
            ? this.props.entities.find(_ => _.id === this.state.selectedEntityId) ?? this.props.suggestedInfo?.suggestedEntities?.find(_ => _.id === this.state.selectedEntityId)
            : undefined;

    private _isAnyItemSelected = () => this.props.customView 
        ? !!this.props.customView?.isSelectionEnabled
        : this._selection.getSelection().length > 0;

    private _getSelectedItems = () => {
        const selectedIds = (this._selection.getSelection() as TSubentity[]).map(_ => _.id);
        return this.props.entities.filter(_ => selectedIds.includes(_.id));
    }

    private _getSelectedSuggestedItems = () => {
        const selectedIds = (this._selection.getSelection() as TSubentity[]).map(_ => _.id);
        return this.props.suggestedInfo?.suggestedEntities?.filter(_ => selectedIds.includes(_.id));
    }

    private _getSelectedItemsIds(): string[] {
        return this._getSelectedItems().map(_ => _.id);
    }

    private _getSelectedSuggestedItemsIds(): string[] {
        return this._getSelectedSuggestedItems()?.map(_ => _.id) ?? [];
    }

    private _getSelectionModeCommands = (displayFields: string[]): IContextualMenuItem[] => {
        const { readOnly, subentityInfo, allowImportedManage, commandsConfig, isArchived, showOnlyExternalCommands } = this.props;
        const { isImportedEntitySelected } = this.state;
        const selectedItems = this._getSelectedItems();
        const suggestedItems = this._getSelectedSuggestedItems();

        const selectedCount = selectedItems.length;

        const actions: SelectedEntitiesActions = {
            exportToCsv: !this._isHidden(commandsConfig?.exportToCsv) ? {
                key: 'exportRows',
                name: commandsConfig?.exportToCsv?.name ?? `Export to CSV`,
                title: MenuTitleBuilder.exportSelectedToCSVTitle(this._getPluralLabel()),
                iconProps: csvImportExportIconProps,
                disabled: (selectedCount < 1 && (!suggestedItems || suggestedItems.length < 1)) || isArchived,
                onClick: () => this._onExportToCsv(displayFields)
            } : undefined,
        };

        const exportCommands = [actions.exportToCsv].filter(notUndefined);
        if (readOnly) {
            return exportCommands;
        }

        const commands = this.props.externalCommands?.selectionModeCommands?.(selectedItems, actions) ?? [];
        if (showOnlyExternalCommands) {
            return commands;
        }

        return [
            ...commands,
            ...exportCommands,
            !this._isHidden(commandsConfig?.delete) ? {
                key: 'deleteRow',
                name: commandsConfig?.delete?.name ?? `Delete`,
                title: selectedCount > 0 && isImportedEntitySelected && !allowImportedManage
                    ? `Imported ${subentityInfo.subentityTypeLabel} can't be deleted`
                    : MenuTitleBuilder.deleteSelectedTitle(this._getPluralLabel()),
                iconProps: { iconName: 'Delete' },
                disabled: !allowImportedManage && isImportedEntitySelected || selectedCount < 1,
                onRender: (item: any) => {
                    // nail to show title for disabled item
                    return <div data-is-focusable title={item.title} className="list-menu-command">
                        <CommandBarButton
                            {...item}
                            text={item.name}
                            styles={{ root: { height: '100%' }, label: { whiteSpace: 'nowrap' }, ...item.buttonStyles }}
                        />
                    </div>;
                },
                onClick: () => this._onDeleteActionClick(this._getSelectedItemsIds()),
            } as IContextualMenuItem : undefined,
            ...(this.props.suggestedInfo?.suggestedEntities?.length && !readOnly
                ? [
                    {
                        key: 'rejectRow',
                        name: `Reject`,
                        iconProps: { iconName: 'Broom' },
                        disabled: !suggestedItems?.length,
                        onClick: () => this.setState({ rejectSuggestedIds: this._getSelectedSuggestedItemsIds() })
                    } as IContextualMenuItem,
                    {
                        key: 'confirmRow',
                        iconProps: { iconName: 'Accept' },
                        name: `Accept`,
                        disabled: !suggestedItems?.length,
                        onClick: () => this.setState({ confirmSuggested: this._getSelectedSuggestedItems() })
                    } as IContextualMenuItem,
                ]
                : [])
        ].filter(notUndefined);
    }

    private _onDeleteActionClick = (removeItemIds: string[]) => {
        const { commandsConfig } = this.props;
        if (commandsConfig?.delete?.action) {
            commandsConfig.delete.action(this._selection.getSelection() as TSubentity[]);
        } else {
            this.setState({ removeItemIds });
        }
    }

    private _getCommands = (): IContextualMenuItem[] => {
        const { readOnly, subentityInfo, commandsConfig, showOnlyExternalCommands } = this.props;
        const { filteredEntities } = this.state;

        const actionsCommand = buildActionsMenuItemWithItems(this._getActionsCommandItems());

        if (readOnly) {
            return [actionsCommand].filter(notUndefined);
        }

        const externalCommands = this.props.externalCommands?.commands?.(filteredEntities) ?? [];
        if (showOnlyExternalCommands) {
            return [
                ...externalCommands,
                actionsCommand,
            ].filter(notUndefined);
        }

        const onAddClick = () => {
            if (commandsConfig?.add?.action) {
                commandsConfig?.add?.action();
            } else {
                this.setState({ action: DialogAction.Add });
                this.props.onEditMenuClick?.(DialogAction.Add);
            }
        }
        return ([
            !this._isHidden(commandsConfig?.add) ? {
                key: 'addRow',
                name: commandsConfig?.add?.name ?? `New ${subentityInfo.subentityTypeLabel}`,
                title: commandsConfig?.add?.title,
                iconProps: { iconName: 'Add' },
                subMenuProps: commandsConfig?.add?.getSubMenuProps?.(onAddClick),
                onClick: onAddClick
            } : undefined,
            !this._isHidden(commandsConfig?.aiGeneration) ? {
                key: 'generateWithAI',
                name: commandsConfig?.aiGeneration?.name ?? `Generate with AI`,
                title: commandsConfig?.aiGeneration?.title,
                iconProps: { iconName: 'PPMXAiGenerated' },
                disabled: commandsConfig?.aiGeneration?.disabled,
                className: 'generate-with-ai',
                onClick: () => commandsConfig?.aiGeneration?.action?.()
            } : undefined,
            this._isImportEnabled()
                ? {
                    key: "importRows",
                    name: `Import ${subentityInfo.subentityTypeLabel}`,
                    iconProps: { iconName: 'PPMXImport' },
                    onClick: () => this.setState({ action: DialogAction.Import })
                } : undefined,
            ...externalCommands,
            actionsCommand,
        ] as IContextualMenuItem[]).filter(notUndefined);
    }

    private _getActionsCommandItems = (): IContextualMenuItem[] => {
        const { readOnly, commandsConfig, externalCommands, customView } = this.props;
        const { filteredEntities } = this.state;
        const exportCommands = [
            !this._isHidden(commandsConfig?.exportToPng)
                ? buildExportToPngMenuItem(this._getPngExportConfig, this.props.pngExporterActions, !filteredEntities?.length)
                : undefined
        ].filter(notUndefined);

        const extensionsCommands = !this.props.readOnly || this.props.allowExtensions
            ? buildExtensionsMenuItem(this.props.extensions, (_) => this.setState({ isOpenExtensionPanel: _ }))
            : [];
            
        if(customView){
            return [
                ...extensionsCommands,
                ...externalCommands?.actionsCommandItems?.(filteredEntities) ?? [],
            ].filter(notUndefined);
        }

        if (readOnly) {
            return [
                ...extensionsCommands,
                ...externalCommands?.actionsCommandItems?.(filteredEntities) ?? [],
                ...exportCommands,
            ].filter(notUndefined);
        }

        const importCommands: IContextualMenuItem[] = [
            !this._isHidden(commandsConfig?.importFromCsv)
                ? {
                    key: 'importRows',
                    name: commandsConfig?.importFromCsv?.name ?? 'Import from CSV',
                    title: MenuTitleBuilder.importFromCSVTitle(this._getPluralLabel()),
                    iconProps: csvImportExportIconProps,
                    onClick: () => this._fileUpload?.click()
                } : undefined
        ].filter(notUndefined);

        return [
            ...extensionsCommands,
            ...externalCommands?.actionsCommandItems?.(filteredEntities) ?? [],
            ...importCommands,
            ...exportCommands
        ].filter(notUndefined);
    }

    private _isImportEnabled = (): boolean => {
        return !!this.props.renderImport;
    }

    private _renderItemMenu = (item: TSubentity) => {
        const { readOnly, subentityInfo, menu, allowImportedManage, commandsConfig: buttonsConfig, parentEntityId, allowExtensions, sourceType } = this.props;
        const { isImportedEntitySelected, selectedCount } = this.state;
        const isReadOnlyItem = (item as unknown as IEditableExtensibleEntity).isEditable === false;
        const isSuggestedItem = (item as unknown as ISuggestedSubentity)?.isSuggested;

        const selectedItems = this._selection.getSelection() as TSubentity[];
        const deleteDisabled = !allowImportedManage && (isImportedEntitySelected || selectedCount < 1);
        const editHidden = readOnly || this._isHidden(buttonsConfig?.edit, [item]) || (isReadOnlyItem && !isSuggestedItem);
        const deleteHidden = readOnly || this._isHidden(buttonsConfig?.delete) || isReadOnlyItem;
        const commands: IContextualMenuItem[] = [
            {
                key: 'updateRow',
                name: editHidden ? 'View' : buttonsConfig?.edit?.name ?? 'Edit',
                iconProps: { iconName: editHidden ? 'View' : 'Edit' },
                onClick: () => {
                    this.setState({ action: DialogAction.Update });
                    this.props.onEditMenuClick?.(DialogAction.Update, item);
                }
            },
            ...(isSuggestedItem ? [] : menu?.getItemCommands?.(selectedItems, item, { filter: this.props.filter?.activeFilter!, prefilter: this.props.filter?.preFilter?.active, search: this.props.search?.search }) ?? []),
            !deleteHidden ? {
                key: 'deleteRow',
                name: 'Delete',
                style: { color: deleteDisabled ? undefined : 'red' },
                iconProps: { iconName: "Delete", style: { color: deleteDisabled ? undefined : 'red' } },
                disabled: deleteDisabled,
                title: selectedCount > 0 && isImportedEntitySelected && !allowImportedManage ?
                    `Imported ${subentityInfo.subentityTypeLabel} can't be deleted` :
                    undefined,
                onClick: () => this._onDeleteActionClick(this._getSelectedItemsIds())
            } : undefined,
            ...(isSuggestedItem && !readOnly
                ? [
                    {
                        key: 'rejectRow',
                        name: `Reject`,
                        iconProps: { iconName: 'Broom' },
                        onClick: () => this.setState({ rejectSuggestedIds: this._getSelectedSuggestedItemsIds() })
                    } as IContextualMenuItem,
                    {
                        key: 'confirmRow',
                        iconProps: { iconName: 'Accept' },
                        name: `Accept`,
                        onClick: () => this.setState({ confirmSuggested: this._getSelectedSuggestedItems() })
                    } as IContextualMenuItem,
                ]
                : [])
        ].filter(notUndefined);

        return commands.length
            ? <RowMenuWithExtensions
                commands={commands}
                context={{ parentEntityId, sourceType }}
                entityType={subentityInfo.subentityType}
                onRefresh={this.props.actions.refreshEntity}
                selection={this._selection}
                item={{ ...item, allowExtensions: !readOnly || allowExtensions }} />
            : null;
    }

    private _buildMenuProps = (displayFields: string[]): IWithViews | IWithColumns | undefined => {
        const { view, hideConfigureColumns, timelineMapProps, canConfigureSelection, commandsConfig, customView } = this.props;

        if (this._isAnyItemSelected()) {
            return undefined;
        }
        if(customView ) {
            return customView.menu;
        }
        if (view) {
            return {
                type: "Views",
                views: view.subViews,
                activeView: view.activeSubView,
                ...view.actions
            };
        }

        if (timelineMapProps) {
            return undefined;
        }

        if (!hideConfigureColumns) {
            return {
                type: "Columns",
                canViewConfigureColumns: canConfigureSelection,
                displayFields,
                onDisplayFieldsChange: canConfigureSelection
                    ? this._getViewConfig().onDisplayFieldsChange
                    : undefined,
                flattenAvailableFieldGroups: commandsConfig?.columns?.flattenAvailableFieldGroups,
                hiddenFields: commandsConfig?.columns?.hiddenFields,
                hideNewFieldButton: commandsConfig?.columns?.hideNewFieldButton,
            };
        }

        return undefined;
    }

    private _buildStylingMenuProps(): Styling | undefined {
        return this._getViewConfig()?.styling;
    }

    private _buildOutlineMenuProps(): IOutline | undefined {
        if (this.props.hierarchy?.isSupportOutline() && !this.props.customView) {
            const pluralLabel = this._getPluralLabel();
            return {
                outlineTitle: () => this.props.commandsConfig?.outline?.title?.() ??
                    `Expand or collapse ${pluralLabel} in the ${pluralLabel} list`,
                expandTitle: `Expand ${pluralLabel} list to make ${pluralLabel} on all hierarchy levels visible`,
                collapseTitle: `Collapse ${pluralLabel} list to make only ${pluralLabel} on the 1st level of hierarchy visible`,
                expandAll: () => this.props.hierarchy?.expandAll(),
                collapseAll: () => this.props.hierarchy?.collapseAll(),
                disabled: this.props.commandsConfig?.outline?.disabled,
            };
        }

        return undefined;
    }

    private _getViewConfig = (): ViewConfig<TSubentity> => {
        const { controlSettings, timelineMapProps, timelineProps, settings, actions, hierarchy, customView } = this.props;
        const isDragDisabled = (_: TSubentity): boolean => hierarchy?.isDragDisabled(_, controlSettings.viewType) ?? true;

        switch (controlSettings.viewType) {
            case Views.Timeline: return {
                styling: timelineProps?.styling,
                onDisplayFieldsChange: this._onTimelineDisplayFieldsChange,
                displayFields: settings.timeline?.displayFields || [],
                draggableEvents: {
                    onDrag: actions.dragEntities,
                    isDragDisabled,
                }
            }
            case Views.List: return {
                styling: this.props.listStyling,
                onDisplayFieldsChange: this._onDisplayFieldsChange,
                displayFields: settings.displayFields,
                draggableEvents: {
                    onDragStart: _ => hierarchy?.onDragStart(_),
                    onDrag: actions.dragEntities,
                    isDragDisabled,
                }
            }
            case Views.TimelineMap: return {
                styling: timelineMapProps?.styling,
                onDisplayFieldsChange: this._onTimelineMapDisplayFieldsChange,
                displayFields: settings.timelineMap?.displayFields || [],
                draggableEvents: {
                    onDrag: actions.dragEntities,
                    isDragDisabled,
                }
            }
            case Views.FlowChart: return {
                displayFields: [],
                draggableEvents: {},
            };
        }

        if(customView)
        {
            return customView.config;
        }

        return {
            displayFields: [],
            draggableEvents: {}
        }
    }

    private _isImported(entity?: TSubentity): boolean {
        return !!entity && isLinkedSubentity(this.props.subentityInfo.subentityType, entity) && entity.sourceType !== SourceType.Ppmx;
    }

    private _onExportToCsv = (displayFields: string[]) => {
        const selectedItems: string[] = this._getSelectedItemsIds();
        const selectedSuggestedItems: string[] = this._getSelectedSuggestedItemsIds();
        if (selectedItems.length || selectedSuggestedItems.length) {
            this.props.actions.exportToFile?.(
                this.props.subentityInfo.subentityType,
                this._getPluralLabel(),
                displayFields,
                selectedItems,
                selectedSuggestedItems,
                this.props.controlSettings.viewType);
        }
        this._selection.setAllSelected(false);
    }

    private _onImportFromCsv = (file: File) => {
        this.props.actions.importFromFile?.(this.props.subentityInfo.subentityType, this.props.subentityInfo.subentityCollectionName, file);
    }

    private _getPluralLabel = (): string => this.props.subentityInfo.pluralSubentityTypeLabel ?? `${this.props.subentityInfo.subentityTypeLabel}s`;

    private _getPngExportConfig = (): PngExportConfig => {
        const { controlSettings } = this.props;
        const { filteredEntities } = this.state;
        const groups = this._suggestedHelper.getGroups(this.props.groups, this.props.entities?.length);
        const listGrouping = this._suggestedHelper.getListGrouping(!!this.props.pngExportDetails, this.props.listGrouping);

        const groupRowsCount = listGrouping?.renderGroupHeader && groups ? groups.length : 0;
        const rowsCount = filteredEntities.length + groupRowsCount;
        const isDetailsView = controlSettings.viewType === Views.List;
        return {
            name: this._getPluralLabel(),
            controlId: getExportControlId(this.props),
            activeView: isDetailsView ? Metadata.ViewTypes.list : Metadata.ViewTypes.timeline,
            elementSelector: isDetailsView ? ListExportElementSelector : undefined,
            rowsCount
        };
    }
}

export const ListExportElementSelector = ".ms-DetailsList div[role='grid']";

const getExportControlId = <TSubentity extends ISubentity>(props: OwnProps<TSubentity>) => `${props.sectionId}_${props.controlId}`;

export default function <TSubentity extends ISubentity>() {
    function mapStateToProps(state: ApplicationState, ownProps: OwnProps<TSubentity> & RouteComponentProps<{}>): StoreProps {
        const subentityType = ownProps.subentityInfo.subentityType;
        const fields = state.fields[subentityType];
        const filters = ownProps.useFilters
            ? FiltersStore.getFilter(state.filters, subentityType, FiltersStore.FilterKeys.Main, ownProps.sourceType)
            : undefined;

        const isReadOnlyMode = isInReadonlyMode(state.user, state.tenant, { isArchived: ownProps.isArchived });
        return {
            fields: fields.allIds.map(_ => fields.byId[_]),
            isFieldsLoading: fields.isLoading,
            isLoading: !!ownProps.entitiesIsLoading,
            pngExportDetails: state.pngExporter.controls[getExportControlId(ownProps)],
            filters: filters?.all,
            extensions: ownProps.parentEntityType ? findExtension(state.extensions, ownProps.parentEntityType, ExtensionPageLocation.Actions, subentityType, ownProps.sourceType) : [],
            canManageConfiguration: contains(state.user.permissions.common, CommonOperations.ConfigurationManage),
            hasSuggestions: Subscription.contains(state.tenant.subscription, PPMFeatures.Suggestions),
            hasAuditLog: Subscription.contains(state.tenant.subscription, PPMFeatures.AuditLog),
            canConfigureSelection: ownProps.isViewSelected
                ? !ownProps.isArchived && contains(state.user.permissions.common, CommonOperations.ConfigurationManage)
                : isReadOnlyMode || !!ownProps.canConfigure
        };
    }

    function mapDispatchToProps(dispatch: Dispatch, ownProps: OwnProps<TSubentity> & RouteComponentProps<{}>): ActionProps {
        const subentityFieldsStore = FieldsStore.actionCreators.forEntity(ownProps.subentityInfo.subentityType, ownProps.parentEntityId, ownProps.actions.refreshEntity);
        return {
            fieldsActions: bindActionCreators(subentityFieldsStore, dispatch),
            notificationsActions: bindActionCreators(NotificationsStore.actionCreators, dispatch),
            pngExporterActions: bindActionCreators(PngExportActionCreators, dispatch),
            filtersActions: bindActionCreators(FiltersStore.actionCreators.forEntity(ownProps.subentityInfo.subentityType), dispatch),
        };
    }

    const WithSearch = withSearch(SubentitiesList);
    const WithFilter = withFilter(WithSearch, useFilterProps);
    const WithSubViews = withSubView<Props<TSubentity> & RouteComponentProps<{}> & IMaybeWithFilter<TSubentity>>((props: Props<TSubentity>) => props.useFilters
        ? <WithFilter {...props} />
        : <SubentitiesList {...props} />);
    const WithStore = connect(mapStateToProps, mapDispatchToProps)(WithSubViews);
    return withRouter<OwnProps<TSubentity>>(WithStore);
}

export function getItemsFromSelection<TEntity extends IBaseEntity>(selection: Selection, ids?: string[]): TEntity[] {
    const items = selection.getItems().map(_ => _ as TEntity);
    return ids === undefined
        ? items
        : items.filter(_ => ids.includes(_.id));
}

type ViewSelectorProps<TSubentity extends ISubentity> = IListProps<TSubentity> & ListUserSettings & {
    timelineProps?: ITimelineProps;
    timelineMapProps?: ITimelineMapProps;
    flowChartProps?: FlowChartProps;
    isFullScreen: boolean;
    customViewRender?: (viewType: Views) => JSX.Element | null;
}

const ViewSelector = <TSubentity extends ISubentity>(props: ViewSelectorProps<TSubentity>) => {
    const { controlSettings, timelineMapProps, timelineProps, flowChartProps, customViewRender } = props;
    return <>
        {controlSettings.viewType === Views.List && <PersistDetailsList
            {...props}
            //scroll event not raised in fullscreen mode, should be reevaluated after fabric update
            isVirtualizationDisabled={props.isVirtualizationDisabled || props.isFullScreen}
        />}
        {controlSettings.viewType === Views.TimelineMap && <PersistTimelineMap
            {...props}
            {...timelineMapProps!}
        />}
        {controlSettings.viewType === Views.Timeline && <PersistTimelineList
            {...props}
            {...timelineProps!}
        />}
        {controlSettings.viewType === Views.FlowChart && <FlowChart
            {...props}
            {...flowChartProps!}
        />}
        {customViewRender?.(controlSettings.viewType)}
    </>
}

const HierarchyViewSelector = <TSubentity extends ISubentity>(props: ViewSelectorProps<TSubentity> & {
    hierarchy: SectionHierarchyManager<TSubentity, SubentitiesListHierarchyContext<TSubentity>>;
}) => {
    const { controlSettings, fields, isFieldFake, fieldValueExtractor, hierarchy, timelineProps, timelineMapProps, flowChartProps, customViewRender: renderCustomView } = props;

    const comparerBuilder: ComparerBuilder<TSubentity> = React.useCallback((orderBy) => {
        const fieldsMap = Metadata.toMap(fields, isFieldFake);
        return SortService.getComparer(fieldsMap, orderBy, isFieldFake, fieldValueExtractor);
    }, [fields, isFieldFake, fieldValueExtractor]);

    const hierarchyProps = {
        comparerBuilder: comparerBuilder,
        hierarchy
    }

    return <>
        {controlSettings.viewType === Views.List && <HierarchyDetailsList
            {...props}
            {...hierarchyProps}
            //scroll event not raised in fullscreen mode, should be reevaluated after fabric update
            isVirtualizationDisabled={props.isVirtualizationDisabled || props.isFullScreen}
        />}
        {controlSettings.viewType === Views.TimelineMap && <HierarchyTimelineMap
            {...props}
            {...timelineMapProps!}
            {...hierarchyProps}
        />}
        {controlSettings.viewType === Views.Timeline && <HierarchyTimelineList
            {...props}
            {...timelineProps!}
            {...hierarchyProps}
        />}
        {controlSettings.viewType === Views.FlowChart && <FlowChart
            {...props}
            {...flowChartProps!}
        />}
        {renderCustomView?.(controlSettings.viewType)}
    </>
}

const useFilterProps = <TSubentity extends ISubentity>(props: Props<TSubentity>): WithFilterProps<TSubentity> => {
    const filterHelper = React.useMemo(
        () => {
            const builderProps = { entities: props.entities, fields: props.fields };
            return props.buildEntityFilterHelper?.(builderProps) ?? new FilterHelper(builderProps);
        }, [props.buildEntityFilterHelper, props.entities, props.fields]);

    return {
        filterHelper: filterHelper,
        canManageConfiguration: props.canManageConfiguration,
        entities: props.entities,
        entityType: props.subentityInfo.subentityType,
        fields: props.fields,
        filters: props.filters || [],
        filtersActions: props.filtersActions,
        location: props.location,
        history: props.history,
        controlSettings: props.controlSettings,
        onSaveSettings: props.onSaveSettings,
        nonFilterableFields: props.nonFilterableFields,
        preFilterItems: props.preFilterItems,
        sourceType: props.sourceType
    }
}